import { useState, createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { plus, multiply, divide, roundUpNumber, roundAndFixedNumber } from 'utils/number';
import {
  requestPlacingPayment,
  requestStripeConfirmPayment,
  requestReceipt,
  requestLoyalty,
  reqUpdatePayLater,
  reqGetCurrentReserveLoyalty,
  reqCheckoutSplitPayment,
  reqConfirmAdyenPayment,
  reqPaymentMethosAdyen,
} from 'services/payment';
import apiUrlProvider, {
  RESULT,
  QR_RESULT,
  CAPTURE_LATER_RESULT,
  SPLIT_WAITING,
  THIRD_PAYMENT_PENDING,
} from 'constants/route-paths';
import { goBackError } from 'constants/error';
import { useAsyncError } from 'utils/hooks';
import { isEmpty } from 'lodash-es';
import {
  PAYMENT_METHODS,
  dynamicQr,
  payLater,
  qrStand as qrType,
  splitAmountPaymentType,
  splitMenuPaymentType,
} from 'constants/payment';
import { setCountryCode, setCurrencyCode } from 'utils/locale';
import { useTranslation } from 'react-i18next';
import { requestCheckHappyHour, requestClearCoupon } from 'services/discount';
import { isProductionMode } from 'utils/environment';
import { SESSION } from 'constants/enum/session';
import WaveLoadingPage from 'components/WaveLoadingPage';
import routeUrlProvider from 'constants/route-paths';
import useErrorMsg from 'utils/errorMessage';
import { PROVIDER } from 'constants/enum/type';

const placingPaymentContext = createContext();
const usePlacingPayment = () => {
  const context = useContext(placingPaymentContext);
  if (context === undefined) {
    throw new Error('usePlacingPayment must be used within a AuthProvider');
  }
  return context;
};

const PlacingPaymentProvider = placingPaymentContext.Provider;
const PlacingPaymentConsumer = placingPaymentContext.Consumer;
const PlacingPayment = (props) => {
  const [fetching, setFetching] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState({});
  const [totalTipPercent, setTotalTipPercent] = useState(0);
  const [includeTip, setIncludeTip] = useState(false);
  const [select, setSelected] = useState();
  const [isSubmitPage, setSubmitPage] = useState(true);
  const [method, setMethod] = useState('');
  const [happyHourError, setHappyHourError] = useState('');
  const [isHappyHour, setIsHappyHour] = useState(false);
  const [error, setError] = useState('');
  const [autoRedirect, setAutoRedirect] = useState(() => sessionStorage.getItem('autoRedirect') === 'true');
  const [autoDelay, setAutoDelay] = useState(() => {
    const stored = sessionStorage.getItem('autoDelay');
    return stored !== null ? parseInt(stored, 10) : null;
  });
  const [customSuccessUrl, setCustomSuccessUrl] = useState(() => sessionStorage.getItem('customSuccessUrl'));
  const isGiropay = window.location.pathname.includes(PAYMENT_METHODS.GIROPAY.toLowerCase());
  const isIdeal = window.location.pathname.includes(PAYMENT_METHODS.IDEAL.toLowerCase());
  const { handleErrorMessageAlert } = useErrorMsg();
  const [adyenPaymentMethods, setAdyenPaymentMethods] = useState({});

  const { push } = useHistory();
  const history = useHistory();
  const throwError = useAsyncError();
  const { t } = useTranslation();
  const { token } = useParams();

  const subTotalAmount = useMemo(() => {
    return (
      paymentInfo?.payload?.checkoutItems?.reduce((sum, item) => {
        return plus(sum, item.totalPrice);
      }, 0) || paymentInfo?.fullAmount
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInfo?.payload?.checkoutItems]);

  const totalAmountExcludedFee = useCallback(() => {
    return paymentInfo.amountExcludedFee || paymentInfo.amountExcludedFee === 0
      ? subTotalAmount || paymentInfo?.fullAmount
      : paymentInfo.fullAmount;
  }, [paymentInfo.amountExcludedFee, paymentInfo.fullAmount, subTotalAmount]);

  const getTipPercentage = () => {
    return divide(totalTipPercent, 100);
  };

  const totalTipSummary = (amount) => {
    const totalPercent = divide(totalTipPercent, 100);
    const totalAmount = multiply(amount, totalPercent);
    return roundAndFixedNumber(totalAmount);
  };

  useEffect(() => {
    const calculateSummaryAmount = async () => {
      const totalPercent = divide(totalTipPercent, 100);
      const totalAmount = multiply(totalAmountExcludedFee(), totalPercent);
      const plusAmount = plus(paymentInfo.amount, totalAmount);
      const summaryAmount = multiply(plusAmount, 100);
      const summaryAmountInt = roundUpNumber(summaryAmount, 0);

      await paymentRequest.update({
        total: {
          label: t('amount'),
          amount: summaryAmountInt,
        },
      });
    };
    if (paymentRequest && paymentInfo?.amount) {
      calculateSummaryAmount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRequest, paymentInfo, totalTipPercent, t]);

  const checkHappyHour = () => {
    //TODO disable discount
    if (paymentInfo.isSplitBill) {
      return;
    }
    return requestCheckHappyHour(token)
      .then(() => {
        setIsHappyHour(true);
      })
      .catch((error) => {
        if (error.statusCode !== 404) {
          setHappyHourError(handleErrorMessageAlert(error));
        }
      });
  };

  const clearCoupon = () => {
    return requestClearCoupon(token).catch(() => {});
  };

  const handleSessionToken = () => {
    sessionStorage?.setItem(SESSION.TOKEN, token);
  };

  const getInitalPlacingPayment = () => {
    localStorage?.removeItem('homeUrl');
    return requestPlacingPayment(token)
      .then(async (response) => {
        const { currency, connectCurrencyCountry } = response;
        setCountryCode(connectCurrencyCountry);
        setCurrencyCode(currency);

        localStorage?.setItem('homeUrl', response.backUrl ?? response.back_url ?? 'https://www.fanz.io/');
        localStorage?.removeItem('storeCode');
        response.storeCode && localStorage?.setItem('storeCode', response.storeCode);

        setPaymentInfo(response);

        handleSessionToken();

        if (response.merchantProvider === PROVIDER.ADYEN) {
          await getAdyenPaymentMethods();
        }

        return response;
      })
      .catch((err) => {
        const { statusCode, data } = err;
        if (statusCode === 400) {
          localStorage?.setItem('homeUrl', data.redirect_url ?? 'https://www.fanz.io/');
          push(`/404/payment-has-been-paid`, { redirectUrl: data.redirect_url });
        } else {
          throwError(t(`${err.messageKey}`), goBackError);
        }
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const placingPayment = () => {
    requestPlacingPayment(token)
      .then(async (response) => {
        const { currency, connectCurrencyCountry } = response;
        setCountryCode(connectCurrencyCountry);
        setCurrencyCode(currency);
        setPaymentInfo({
          ...response,
          enabledPaymentMethods: paymentInfo.enabledPaymentMethods,
        });
        handleSessionToken();
        return response;
      })
      .catch((err) => {
        throwError(t(`${err.messageKey}`), goBackError);
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const getAdyenPaymentMethods = async () => {
    reqPaymentMethosAdyen(token).then((res) => setAdyenPaymentMethods(res.paymentMethods));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    if (token) {
      if (!isProductionMode()) {
        if (!isGiropay && !isIdeal) {
          await clearCoupon();
        }
        await checkHappyHour();
      }
      await getInitalPlacingPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isGiropay, isIdeal]);

  const handlePendingStripeCheckout = (payload, token) => {
    const { method, payment_intent_id, cashback_email } = payload;

    const customPayload = {
      method,
      payment_intent_id,
      cashback_email,
    };

    requestStripeConfirmPayment(token, customPayload)
      .then((_) => {
        const pathname = routeUrlProvider.getForLink(THIRD_PAYMENT_PENDING, { token });
        history.push(pathname);
        setPaying(false);
      })
      .catch((err) => {
        setPaying(false);
        handleError(err);
      });
  };

  const handleStripeCheckout = (payload) => {
    setPaying(true);
    const { type, fullAmount } = paymentInfo;
    const { method, payment_intent_id, cashback_email, payment_method } = payload;
    const tipPercentage = multiply(getTipPercentage(), 100);

    const customPayload = {
      method,
      payment_intent_id,
      cashback_email,
    };

    if (type === payLater) {
      reqUpdatePayLater(token, { payment_method })
        .then((response) => handlePayLaterSuccess(response, true))
        .catch((err) => {
          setPaying(false);
          handleError(err);
        });
    } else if ([splitAmountPaymentType, splitMenuPaymentType].includes(type)) {
      reqCheckoutSplitPayment(token, {
        payment_method_id: payment_method,
        tip_percentage: divide(tipPercentage, 100),
      })
        .then((response) => {
          const data = {
            paymentInfo,
            tip_amount: totalTipSummary(fullAmount),
            tip_percentage: tipPercentage,
            anotherEmail: payload.email,
            paymentData: {
              method: payload.method,
              payment_intent_id: payload.payment_intent_id,
            },
            redirect_url: response.redirect_url,
          };
          push(apiUrlProvider.getForRoute(SPLIT_WAITING), data);
          setPaying(false);
        })
        .catch((err) => {
          setPaying(false);
          handleError(err);
        });
    } else {
      requestStripeConfirmPayment(token, customPayload)
        .then((response) => {
          if (response.isSplit) {
            const data = {
              paymentInfo,
              tip_amount: totalTipSummary(fullAmount),
              tip_percentage: tipPercentage,
              anotherEmail: payload.email,
              paymentData: {
                method: payload.method,
                payment_intent_id: payload.payment_intent_id,
              },
              redirect_url: response.redirect_url,
            };
            push(apiUrlProvider.getForRoute(SPLIT_WAITING), data);
            setPaying(false);
          } else {
            const redirect_url = response.redirect_url;
            handleSuccess(redirect_url);
          }
        })
        .catch((err) => {
          setPaying(false);
          handleError(err);
        });
    }
  };

  const handleAdyenConfirmPayment = (redirectResult) => {
    if (!redirectResult) return;

    const details = { redirectResult };

    reqConfirmAdyenPayment(token, { details })
      .then((res) => {
        const redirectUrl = res.redirect_url;
        handleSuccess(redirectUrl, { preAuth: res?.preAuth === true });
      })
      .catch((err) => {
        handleError(err);
      });
  };

  const handleSuccess = (redirect_url, options = {}) => {
    const { type, taxAmount, merchantEmail } = paymentInfo;
    const homeUrl = localStorage.getItem('homeUrl') ?? '';
    if (type === payLater || options.preAuth === true) {
      handlePayLaterSuccess({ redirect_url }, true, options.preAuth === true);
    } else {
      Promise.all([requestReceipt(token), requestLoyalty(token)])
        .then(([receiptRes, loyaltyRes]) => {
          const {
            id,
            amount,
            cashbackTo,
            cashbackAmount,
            qrStand,
            date,
            authorizationDate,
            captureDate,
            loyaltyReward,
            reviewEngine,
            logoImage,
            companyName,
            address,
            tip_amount,
            charge_amount,
            tip_percentage,
            discount,
            splitBill,
            receiptUrl,
            showReceiptQr,
            payload,
            extraFee,
            orderDetails,
          } = receiptRes;

          // Prepare successUrl: customSuccessUrl (from query-param) takes priority
          console.log('[handleSuccess] customSuccessUrl:', customSuccessUrl, 'redirect_url:', redirect_url, 'homeUrl:', homeUrl, 'autoRedirect:', autoRedirect, 'autoDelay:', autoDelay);
          let finalSuccessUrl = customSuccessUrl || redirect_url || homeUrl;
          if (autoRedirect && finalSuccessUrl) {
            try {
              const url = new URL(finalSuccessUrl);
              url.searchParams.set('autoRedirect', 'true');
              finalSuccessUrl = url.toString();
            } catch (e) {
              // If URL parsing fails, just use the original URL
            }
          }

          const receipt = {
            pin: qrStand?.pin,
            id,
            cashbackTo,
            amount,
            cashbackAmount,
            image: logoImage,
            companyName: type === qrType ? qrStand.name : companyName,
            companyAddress: address,
            createdAt: date,
            description: qrStand?.description,
            loyalties: loyaltyRes,
            loyaltyReward,
            reviewEngine,
            successUrl: finalSuccessUrl,
            failUrl: redirect_url ?? homeUrl,
            autoRedirect,
            autoDelay,
            tip_amount,
            charge_amount,
            payload,
            totalTipPercent: multiply(tip_percentage, 100),
            taxAmount: Number(taxAmount),
            paymentType: type,
            discount,
            extraFee,
            subTotal: totalAmountExcludedFee(),
            merchantEmail,
            type: isEmpty(splitBill) ? 'success' : 'pending',
            splitBill,
            receiptUrl,
            showReceiptQr,
            orderDetails,
            authorizationDate,
            captureDate,
            createdAt:
              options.preAuth === true ? authorizationDate || captureDate || date : captureDate || date,
            isPreAuth: options.preAuth === true,
          };

          // Persist receipt in sessionStorage so it survives page reloads (e.g. 3DS redirects)
          sessionStorage.setItem('lastReceiptState', JSON.stringify(receipt));
          push(apiUrlProvider.getForRoute(RESULT), receipt);
          setPaying(false);
        })
        .catch((err) => {
          setPaying(false);
          const { statusCode } = err;
          throwError(t(`${err.messageKey}`), statusCode);
        });
    }
  };

  const handleSuccessEvent = (response = {}) => {
    const { paymentData } = response;
    const { type, successUrl, failUrl, taxAmount, extraFee, merchantEmail } = paymentInfo;
    const homeUrl = localStorage.getItem('homeUrl') ?? '';

    // Prepare successUrl: customSuccessUrl (from query-param) takes priority
    console.log('[handleSuccessEvent] customSuccessUrl:', customSuccessUrl, 'successUrl:', successUrl, 'homeUrl:', homeUrl, 'autoRedirect:', autoRedirect, 'autoDelay:', autoDelay);
    let finalSuccessUrl = customSuccessUrl || successUrl || homeUrl;
    if (autoRedirect && finalSuccessUrl) {
      try {
        const url = new URL(finalSuccessUrl);
        url.searchParams.set('autoRedirect', 'true');
        finalSuccessUrl = url.toString();
      } catch (e) {
        // If URL parsing fails, just use the original URL
      }
    }

    Promise.all([requestReceipt(token), requestLoyalty(token)])
      .then(([receiptRes]) => {
        const {
          id,
          amount,
          cashbackTo,
          cashbackAmount,
          qrStand,
          date,
          authorizationDate,
          captureDate,
          loyaltyReward,
          reviewEngine,
          logoImage,
          companyName,
          address,
          tip_amount,
          charge_amount,
          tip_percentage,
          discount,
          receiptUrl,
          showReceiptQr,
          orderDetails,
        } = receiptRes;
        push(apiUrlProvider.getForRoute(RESULT), {
          pin: qrStand?.pin,
          id,
          cashbackTo,
          amount,
          cashbackAmount,
          image: logoImage,
          companyName: type === qrType ? qrStand.name : companyName,
          companyAddress: address,
          createdAt: captureDate || date,
          description: qrStand?.description,
          paymentData,
          loyalties: [],
          loyaltyReward,
          reviewEngine,
          successUrl: finalSuccessUrl,
          autoRedirect,
          autoDelay,
          failUrl: failUrl ?? homeUrl,
          type: 'success',
          tip_amount,
          charge_amount,
          totalTipPercent: multiply(tip_percentage, 100),
          taxAmount: Number(taxAmount),
          paymentType: type,
          discount,
          extraFee,
          subTotal: totalAmountExcludedFee(),
          merchantEmail,
          receiptUrl,
          showReceiptQr,
          orderDetails,
          authorizationDate,
          captureDate,
        });
        setPaying(false);
      })
      .catch(() => {
        setPaying(false);
      });

    return response;
  };

  const handlePayLaterSuccess = (response, payInDevice = false, isPreAuth = false) => {
    const { amount, name, companyName, logoImage, successUrl, success_url } = paymentInfo;
    const paymentData = {
      amount,
      name,
      successUrl: customSuccessUrl || response?.redirect_url || successUrl || success_url || localStorage.getItem('homeUrl') || null,
      payInDevice,
      companyName,
    };

    requestLoyalty(token)
      .then((loyaltyRes) => {
        if (loyaltyRes.length > 0) {
          reqGetCurrentReserveLoyalty(token).then((rewardRes) => {
            push(apiUrlProvider.getForRoute(CAPTURE_LATER_RESULT), {
              type: 'success',
              loyalties: loyaltyRes,
              paymentData,
              tip_amount: totalTipSummary(amount),
              image: logoImage,
              loyaltyReward: rewardRes.currentReward,
              isPreAuth,
            });
            setPaying(false);
          });
        } else {
          push(apiUrlProvider.getForRoute(CAPTURE_LATER_RESULT), {
            type: 'success',
            loyalties: loyaltyRes,
            paymentData,
            image: logoImage,
            tip_amount: totalTipSummary(amount),
            isPreAuth,
          });
          setPaying(false);
        }
      })
      .catch((err) => {
        setPaying(false);
        const { statusCode } = err;
        throwError(t(`${err.messageKey}`), statusCode);
      });
  };

  const handleError = (err = {}) => {
    const { message, data, statusCode } = err;
    const { failUrl, type, amount, name, companyName } = paymentInfo;
    if (type === qrType || type === dynamicQr) {
      push({
        pathname: apiUrlProvider.getForRoute(QR_RESULT),
        state: {
          type: 'error',
          message,
          qrName: data?.qr_name,
          amount,
          companyName,
        },
      });
    } else if (type === payLater) {
      push({
        pathname: apiUrlProvider.getForRoute(CAPTURE_LATER_RESULT),
        state: {
          type: 'error',
          message,
          name,
          amount,
          redirectUrl: failUrl,
          companyName,
        },
      });
    } else if (statusCode === 422) {
      setError(message);
    } else {
      push({
        pathname: apiUrlProvider.getForRoute(RESULT),
        state: {
          type: 'error',
          message,
          amount,
          name,
          companyName,
          redirectUrl: data ? data.redirect_url : failUrl,
        },
      });
    }
    return err;
  };

  if (fetching) {
    return <WaveLoadingPage />;
  }

  return (
    <PlacingPaymentProvider
      value={{
        setPaymentRequest,
        paymentInfo,
        fetching,
        paying,
        setPaying,
        handleStripeCheckout,
        paymentRequest,
        handleSuccess,
        handleSuccessEvent,
        handlePayLaterSuccess,
        setTotalTipPercent,
        totalTipPercent,
        totalTipSummary,
        setIncludeTip,
        includeTip,
        select,
        setSelected,
        isSubmitPage,
        setSubmitPage,
        handleError,
        method,
        setMethod,
        placingPayment,
        happyHourError,
        setIsHappyHour,
        isHappyHour,
        setHappyHourError,
        error,
        setError,
        getTipPercentage,
        totalAmountExcludedFee,
        isHeadSplitBill: paymentInfo.isSplitBill && paymentInfo.isHead,
        isSplitBill: paymentInfo.isSplitBill,
        setFetching,
        handlePendingStripeCheckout,
        handleAdyenConfirmPayment,
        setPaymentInfo,
        adyenPaymentMethods,
        autoRedirect,
        setAutoRedirect,
        autoDelay,
        setAutoDelay,
        customSuccessUrl,
        setCustomSuccessUrl,
      }}
      {...props}
    />
  );
};
export { PlacingPayment, PlacingPaymentConsumer, usePlacingPayment, PlacingPaymentProvider };
